import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import styled from 'styled-components';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import Icon from '@spark/ui-components/components/Icon';
import { toApiDateTime } from './descargaDateTime';

const isEmptyDisplayValue = (value) => value === null || value === undefined || value === '' || value === '-';

const toDateTimeLocalValue = (value) => {
  if (!value || value === '-') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const onlyDigits = (value, maxLength) => String(value || '').replace(/\D/g, '').slice(0, maxLength);

const buildInitialValues = (row) => ({
  quantidadePaletes: isEmptyDisplayValue(row?.quantidadePaletes) ? '' : onlyDigits(row.quantidadePaletes, 6),
  dataAgendamento: toDateTimeLocalValue(row?.dataAgendamentoDescargaOriginal),
  chegada: toDateTimeLocalValue(row?.chegadaDescargaOriginal),
  saida: toDateTimeLocalValue(row?.saidaDescargaOriginal),
  observacoes: isEmptyDisplayValue(row?.observacoesDescargaOriginal ?? row?.observacoesDescarga)
    ? ''
    : String(row?.observacoesDescargaOriginal ?? row?.observacoesDescarga)
});

const DateTimeLocalField = ({ label, value, onChange, onClear, disabled }) => (
  <Form.Group className="mb-3">
    <Form.Label>{label}</Form.Label>
    <DateTimeInputWrap>
      <Form.Control
        type="datetime-local"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
      {value ? (
        <ClearDateButton
          type="button"
          aria-label={`Limpar ${label}`}
          onClick={onClear}
          disabled={disabled}
        >
          <Icon name="close" size={14} />
        </ClearDateButton>
      ) : null}
    </DateTimeInputWrap>
  </Form.Group>
);

DateTimeLocalField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

DateTimeLocalField.defaultProps = {
  value: '',
  disabled: false
};

const DescargaEdicaoRapidaModal = ({
  show,
  row,
  loading,
  onClose,
  onSubmit
}) => {
  const initialValues = useMemo(() => buildInitialValues(row), [row]);
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const identificador = row?.numeroCarga && row.numeroCarga !== '-'
    ? row.numeroCarga
    : row?.protocoloCarga || row?.codigoCarga || '';

  const handleChange = (name, value) => {
    setValues((current) => ({
      ...current,
      [name]: value
    }));
  };

  const handleClearDate = (name) => {
    setValues((current) => ({
      ...current,
      [name]: ''
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    // Quantidade de paletes so vira manual quando alterada; sem alteracao o backend
    // preserva o valor atual (manual ou PED_NUMERO_PALETES do TMS).
    const quantidadePaletesAlterada = values.quantidadePaletes !== initialValues.quantidadePaletes;

    onSubmit({
      dataAgendamento: toApiDateTime(values.dataAgendamento),
      chegada: toApiDateTime(values.chegada),
      saida: toApiDateTime(values.saida),
      observacoes: values.observacoes ? values.observacoes : null,
      dataAgendamentoManual: true,
      chegadaManual: true,
      saidaManual: true,
      ...(quantidadePaletesAlterada ? {
        quantidadePaletes: values.quantidadePaletes === '' ? null : Number(values.quantidadePaletes),
        quantidadePaletesManual: true
      } : {})
    });
  };

  return (
    <Modal show={show} onHide={onClose} animation={false} size="lg" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Editar descarga</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {identificador ? <ModalSubtitle>Descarga {identificador}</ModalSubtitle> : null}
          <Row>
            <Col md={4}>
              <DateTimeLocalField
                label="Data Agendamento"
                value={values.dataAgendamento}
                onChange={(value) => handleChange('dataAgendamento', value)}
                onClear={() => handleClearDate('dataAgendamento')}
                disabled={loading}
              />
            </Col>
            <Col md={4}>
              <DateTimeLocalField
                label="Chegada"
                value={values.chegada}
                onChange={(value) => handleChange('chegada', value)}
                onClear={() => handleClearDate('chegada')}
                disabled={loading}
              />
            </Col>
            <Col md={4}>
              <DateTimeLocalField
                label="Saida"
                value={values.saida}
                onChange={(value) => handleChange('saida', value)}
                onClear={() => handleClearDate('saida')}
                disabled={loading}
              />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3" controlId="descarga-edicao-quantidade-paletes">
                <Form.Label>Quantidade de Paletes/Gaiolas</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  value={values.quantidadePaletes}
                  onChange={(event) => handleChange('quantidadePaletes', onlyDigits(event.target.value, 6))}
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group>
            <Form.Label>Observacoes</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={values.observacoes}
              onChange={(event) => handleChange('observacoes', event.target.value)}
              disabled={loading}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <ButtonWithLoading variant="outline-secondary" type="button" onClick={onClose} disabled={loading}>
            Cancelar
          </ButtonWithLoading>
          <ButtonWithLoading variant="primary" type="submit" loading={loading || undefined} disabled={loading}>
            Salvar
          </ButtonWithLoading>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

DescargaEdicaoRapidaModal.propTypes = {
  show: PropTypes.bool,
  row: PropTypes.shape(),
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

DescargaEdicaoRapidaModal.defaultProps = {
  show: false,
  row: null,
  loading: false
};

export default DescargaEdicaoRapidaModal;

const ModalSubtitle = styled.div`
  margin-bottom: 16px;
  color: #6c757d;
  font-size: 13px;
`;

const DateTimeInputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ClearDateButton = styled.button`
  position: absolute;
  right: 36px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    color: #343a40;
  }
`;
